import { c as _c } from "react/compiler-runtime";
import { CLIENT_PUBLIC_KEY_HEADER, checkIsInIframe, isAlphanumeric } from '@onefootprint/core';
import { useRequestError } from '@onefootprint/fetch-hosted/use-request-error';
import { checkIsSocialMediaBrowser, getLogger, InitShimmer, setTenantLogContext, shouldRecordTenantSession, useGetOnboardingConfig } from '@onefootprint/idv';
import type { OnboardingSessionConfig, VerifyV1SdkArgs } from '@onefootprint/request-types';
import type { IdDocOutcome, IdvBootstrapData, OverallOutcome } from '@onefootprint/types';
import { useToast } from '@onefootprint/ui';
import useBifrostMachine from 'src/hooks/use-bifrost-machine';
import useProps from './use-props';
const {
  logError,
  logInfo,
  logTrack
} = getLogger({
  location: 'bifrost-init'
});
const logPopulatedBootstrapKeys = (bootstrapData: IdvBootstrapData): void => {
  const populatedBootstrapKeys = Object.entries(bootstrapData).filter(([_key, value]) => Array.isArray(value) ? value.length > 0 : !!value).map(([key]) => key).join(',');
  if (populatedBootstrapKeys) {
    logTrack(`Populated bootstrap keys ${populatedBootstrapKeys}`);
  }
};
const isPropsSaved = (context: Record<string, unknown>) => {
  const {
    bootstrapData,
    showCompletionPage,
    showLogo,
    l10n,
    authToken
  } = context;
  return bootstrapData !== undefined && showCompletionPage !== undefined && showLogo !== undefined && l10n !== undefined && authToken !== undefined;
};
const setupLogger = ({
  config
}: {
  config: OnboardingSessionConfig;
}) => {
  if (config.isLive && config.flags.doNotRecordTenantOrgIdOnLogRocket) {
    logInfo(`Session recording disabled for org ${config.orgId}`);
  }
  if (shouldRecordTenantSession(config)) {
    setTenantLogContext(config, {
      application_id: String(process.env.NEXT_PUBLIC_DDOG_RUM_APPLICATION_BIFROST),
      iframe: !!checkIsInIframe(),
      isAppClipEnabled: config.isAppClipEnabled,
      isInstantAppEnabled: config.isInstantAppEnabled,
      socialMedia: checkIsSocialMediaBrowser()
    });
  }
};
const Init = () => {
  const $ = _c(18);
  const [state, send] = useBifrostMachine();
  const {
    authToken: authTokenContext,
    publicKey: publicKeyContext
  } = state.context;
  const {
    getErrorCode
  } = useRequestError();
  let t0;
  if ($[0] !== publicKeyContext) {
    t0 = publicKeyContext ? {
      [CLIENT_PUBLIC_KEY_HEADER]: publicKeyContext
    } : undefined;
    $[0] = publicKeyContext;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const obConfigAuth = t0;
  const {
    toast
  } = useToast();
  let t1;
  if ($[2] !== authTokenContext || $[3] !== obConfigAuth) {
    t1 = {
      obConfigAuth,
      authToken: authTokenContext
    };
    $[2] = authTokenContext;
    $[3] = obConfigAuth;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== send) {
    t2 = {
      onSuccess: config => {
        setupLogger({
          config
        });
        send({
          type: "initContextUpdated",
          payload: {
            config: {
              ...config
            }
          }
        });
      },
      onError: err => {
        logError("Fetching onboarding config failed", err);
        send({
          type: "configRequestFailed"
        });
      }
    };
    $[5] = send;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  useGetOnboardingConfig(t1, t2);
  let t3;
  if ($[7] !== send || $[8] !== state.context || $[9] !== toast) {
    t3 = props => {
      if (isPropsSaved(state.context)) {
        return;
      }
      const {
        authToken: t4,
        isComponentsSdk: t5,
        l10n: t6,
        options: t7,
        publicKey: t8,
        fixtureResult,
        sandboxId,
        documentFixtureResult,
        shouldRelayToComponents
      } = props;
      const authToken = t4 === undefined ? "" : t4;
      const isComponentsSdk = t5 === undefined ? false : t5;
      const l10n = t6 === undefined ? {} : t6;
      const options = t7 === undefined ? {} : t7;
      const publicKey = t8 === undefined ? "" : t8;
      const bootstrapData = (props?.bootstrapData || props?.userData || {}) as IdvBootstrapData;
      const {
        showCompletionPage: t9,
        showLogo: t10
      } = options || {};
      const showCompletionPage = t9 === undefined ? false : t9;
      const showLogo = t10 === undefined ? false : t10;
      const isValidPredefinedSandboxId = sandboxId && isAlphanumeric(sandboxId);
      if (sandboxId && !isValidPredefinedSandboxId) {
        toast({
          title: "Invalid sandbox id provided",
          description: "The sandbox id can only contain alphanumeric characters. Resetting the sandbox id.",
          variant: "error"
        });
      }
      send({
        type: "initContextUpdated",
        payload: {
          authToken,
          bootstrapData,
          isComponentsSdk,
          l10n,
          publicKey,
          showCompletionPage,
          showLogo,
          fixtureResult: fixtureResult as OverallOutcome,
          documentFixtureResult: documentFixtureResult as IdDocOutcome,
          sandboxId: isValidPredefinedSandboxId ? sandboxId : undefined,
          shouldRelayToComponents
        }
      });
      logPopulatedBootstrapKeys(bootstrapData);
    };
    $[7] = send;
    $[8] = state.context;
    $[9] = toast;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== getErrorCode || $[12] !== send) {
    t4 = error => {
      console.error(error);
      const errorCode = getErrorCode(error);
      if (errorCode === "E118") {
        send({
          type: "sessionExpired"
        });
        return;
      }
      logError(`Bifrost init failed, couldn't fetch props ${JSON.stringify(error)}`);
      send({
        type: "initError"
      });
    };
    $[11] = getErrorCode;
    $[12] = send;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== t3 || $[15] !== t4) {
    t5 = {
      onSuccess: t3,
      onError: t4
    };
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  useProps(t5);
  let t6;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <InitShimmer />;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  return t6;
};
export default Init;